import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { SlidersHorizontalIcon, XIcon } from 'lucide-react';
import { useSession } from '../contexts/SessionContext';
import { CHAMPION_SHIRT_PATH } from '../data/site';
const pages = [{
  label: 'Home',
  path: '/'
}, {
  label: 'Dashboard',
  path: '/dashboard'
}, {
  label: 'Product',
  path: CHAMPION_SHIRT_PATH
}, {
  label: 'Email',
  path: '/email'
}];
export function PreviewToggles() {
  const session = useSession();
  const navigate = useNavigate();
  const {
    pathname
  } = useLocation();
  const [open, setOpen] = useState(true);
  const setLoggedIn = (value: boolean) => {
    if (value) {
      session.login();
      if (pathname === '/login') navigate(CHAMPION_SHIRT_PATH);
    } else {
      session.logout();
      if (pathname === '/dashboard') navigate('/');
    }
  };
  const toggles = [{
    id: 'logged-in',
    label: 'Logged in',
    value: session.isLoggedIn,
    onChange: setLoggedIn
  }, {
    id: 'eligible',
    label: 'Champion family',
    hint: session.championEligible ? 'Signed in as David' : 'Signed in as Morgan',
    value: session.championEligible,
    onChange: (v: boolean) => {
      session.setChampionEligible(v);
      session.resetChampionBanner();
    }
  }, {
    id: 'incomplete',
    label: 'Incomplete registration',
    value: session.hasIncompleteRegistration,
    onChange: session.setHasIncompleteRegistration
  }];
  return <div className="fixed bottom-5 right-5 z-50 font-sans" data-id="element-156" data-mp="186jrxq9uj9of">
      <AnimatePresence initial={false} mode="wait" data-id="element-157" data-mp="145hsp9fxixmg">
        {open ? <motion.div key="panel" initial={{
        opacity: 0,
        scale: 0.96,
        y: 8
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 8
      }} transition={{
        duration: 0.18,
        ease: [0.23, 1, 0.32, 1]
      }} style={{
        transformOrigin: 'bottom right'
      }} className="w-72 rounded-xl border border-gray-200 bg-white p-4 text-navy shadow-xl" role="region" aria-label="Preview toggles" data-id="element-158" data-mp="z7g6ifn7ii6r">
            <div className="flex items-center justify-between" data-id="element-159" data-mp="33po46vt65ph3">
              <p className="font-bold" data-id="element-160" data-mp="33n1oua01e5ob">Preview states</p>
              <button onClick={() => setOpen(false)} aria-label="Hide preview toggles" className="flex h-7 w-7 items-center justify-center rounded text-navy/60 transition-colors duration-150 hover:bg-gray-100 hover:text-navy" data-id="element-161" data-mp="38pagn316b1ou">
                <XIcon className="h-4 w-4" data-id="element-162" data-mp="3mgfcdu1f79tw" />
              </button>
            </div>

            <p className="mt-3 text-xs font-semibold text-navy/60" data-id="element-163" data-mp="3re7ax6wklu72">Page</p>
            <div className="mt-1.5 grid grid-cols-2 gap-1 rounded-lg bg-gray-100 p-1" role="group" aria-label="Page" data-id="element-164" data-mp="2b0gv52w39hhl">
              {pages.map(p => {
            const active = pathname === p.path;
            return <button key={p.path} onClick={() => navigate(p.path)} aria-pressed={active} className={`whitespace-nowrap rounded-md px-2 py-1.5 text-sm font-semibold transition-colors duration-150 ${active ? 'bg-white text-navy shadow-sm' : 'text-navy/60 hover:text-navy'}`} data-id="element-165" data-mp="28f9eyneq2yhq">
                    {p.label}
                  </button>;
          })}
            </div>

            <ul className="mt-4 space-y-3" data-id="element-166" data-mp="1ks1c0c4ro1iv">
              {toggles.map(t => <li key={t.id} className="flex items-center justify-between gap-3" data-id="element-167" data-mp="1yimwg59vcjiv">
                  <label htmlFor={t.id} className="min-w-0 cursor-pointer" data-id="element-168" data-mp="1tqwebd4owrgw">
                    <span className="block text-sm font-semibold" data-id="element-169" data-mp="161akz6b016gt">{t.label}</span>
                    {t.hint && <span className="block text-xs text-navy/55" data-id="element-170" data-mp="112h7hdvppfgn">{t.hint}</span>}
                  </label>
                  <button id={t.id} role="switch" aria-checked={t.value} onClick={() => t.onChange(!t.value)} className={`relative h-6 w-11 shrink-0 rounded-full transition-colors duration-150 ${t.value ? 'bg-go' : 'bg-gray-300'}`} data-id="element-171" data-mp="1epg6cknwtobb">
                    <span className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform duration-150 ${t.value ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-172" data-mp="1tff0mcccgfli" />
                  </button>
                </li>)}
            </ul>

            {session.championBannerDismissed && <button onClick={session.resetChampionBanner} className="mt-4 w-full rounded-md border border-gray-200 py-1.5 text-sm font-semibold transition-colors duration-150 hover:bg-gray-50" data-id="element-173" data-mp="2cc6pd1annmwy">
                Show champion banner again
              </button>}
          </motion.div> : <motion.button key="fab" initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.96
      }} transition={{
        duration: 0.15,
        ease: [0.23, 1, 0.32, 1]
      }} onClick={() => setOpen(true)} className="flex items-center gap-2 rounded-full bg-navy px-4 py-3 text-sm font-semibold text-white shadow-xl transition-colors duration-150 hover:bg-navy-deep" data-id="element-174" data-mp="2qi8cn0hwgq3k">
            <SlidersHorizontalIcon className="h-4 w-4" aria-hidden data-id="element-175" data-mp="356v39ycz94a3" />
            Preview states
          </motion.button>}
      </AnimatePresence>
    </div>;
}